import type {ReferralCardModel} from "./referralCard";
import type {ReferrerRewardStory} from "./referrerReward";

/**
 * What the transfer-success screen's referral line renders (ui#1180). `null` from
 * {@link referralPrompt} means the line is ABSENT — never a placeholder, a disabled control or a
 * sign-in nag.
 */
export type ReferralPrompt = {
	/**
	 * The canonical `?ref=` share link, taken verbatim from {@link ReferralCardModel.shareLink} — the
	 * same link the account card offers. Never rebuilt here.
	 */
	url: string;
	/**
	 * Which reward the sentence names (ui#1693), forwarded from the card model rather than decided
	 * again here — the success line and the account card are one promise made on two screens.
	 */
	story: ReferrerRewardStory;
};

/**
 * Whether the success screen offers the sender their referral link, and which link that is (ui#1180).
 *
 * React-free by mandate (epic #26). Three rules live here rather than in the view:
 * <ul>
 *   <li><b>Signed-in accounts only.</b> This product bootstraps an ANONYMOUS Firebase session for
 *       every visitor, so "has a session" is not "has an account" — and an account is what the api
 *       issues a referral code to. An anonymous sender is offered nothing at all: a placeholder link
 *       copied from this screen would attribute nobody, and a sign-in prompt would turn the moment
 *       the transfer succeeded into a nag.</li>
 *   <li><b>No summary, no line.</b> A null model is both "still loading" and "the request failed";
 *       neither is a state to render a copyable link in.</li>
 *   <li><b>The current site only.</b> The account model contains only the browser-origin link, so this
 *       surface cannot advertise another brand or require changes when another brand is added.</li>
 * </ul>
 */
export function referralPrompt(
	signedIn: boolean,
	model: ReferralCardModel | null,
): ReferralPrompt | null {
	if (!signedIn || model === null) {
		return null;
	}
	const link = model.shareLink;
	return link === null ? null : {url: link.url, story: model.rewardStory};
}
